import { useState } from 'react';
import { Image, Link, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { convertGoogleDriveUrl, isGoogleDriveUrl } from '../../lib/imageUtils';

/**
 * ImageInsertDialog - Dialog for inserting images with caption and courtesy
 * 
 * Features:
 * - Image URL input with Google Drive auto-conversion
 * - Caption field (optional, supports hyperlink)
 * - Image courtesy/credit field (optional, supports hyperlink)
 * - Live preview of the image
 */
export function ImageInsertDialog({ open, onOpenChange, onInsert }) {
  const [imageUrl, setImageUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [captionLink, setCaptionLink] = useState('');
  const [courtesy, setCourtesy] = useState('');
  const [courtesyLink, setCourtesyLink] = useState('');
  const [imageError, setImageError] = useState(false);
  const [showCaptionLink, setShowCaptionLink] = useState(false);
  const [showCourtesyLink, setShowCourtesyLink] = useState(false);

  // Get display URL (convert Google Drive if needed)
  const displayUrl = imageUrl ? convertGoogleDriveUrl(imageUrl) : '';
  const isGoogleDrive = imageUrl && isGoogleDriveUrl(imageUrl);

  const handleInsert = () => {
    if (!displayUrl) return;

    // Build the figure HTML with caption and courtesy
    let figureHtml = '<figure class="image-figure" style="text-align: center; margin: 1.5rem 0;">';
    figureHtml += `<img src="${displayUrl}" alt="${caption || 'Article image'}" style="max-width: 100%; height: auto; border-radius: 4px;" />`;
    
    // Add figcaption if there's caption or courtesy
    if (caption || courtesy) {
      figureHtml += '<figcaption style="margin-top: 0.5rem; font-size: 0.875rem; color: #64748b; text-align: center;">';
      
      if (caption) {
        if (captionLink) {
          figureHtml += `<span class="image-caption"><a href="${captionLink}" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">${caption}</a></span>`;
        } else {
          figureHtml += `<span class="image-caption">${caption}</span>`;
        }
      }
      
      if (caption && courtesy) {
        figureHtml += '<br/>';
      }
      
      if (courtesy) {
        figureHtml += '<span class="image-courtesy" style="font-style: italic;">Image courtesy: ';
        if (courtesyLink) {
          figureHtml += `<a href="${courtesyLink}" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">${courtesy}</a>`;
        } else {
          figureHtml += courtesy;
        }
        figureHtml += '</span>';
      }
      
      figureHtml += '</figcaption>';
    }
    
    figureHtml += '</figure>';

    onInsert(figureHtml);
    resetForm();
    onOpenChange(false);
  };

  const resetForm = () => {
    setImageUrl('');
    setCaption('');
    setCaptionLink('');
    setCourtesy('');
    setCourtesyLink('');
    setImageError(false);
    setShowCaptionLink(false);
    setShowCourtesyLink(false);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Image className="h-5 w-5" />
            Insert Image
          </DialogTitle>
          <DialogDescription>
            Add an image with optional caption and image courtesy attribution.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
          {/* Image URL */}
          <div className="space-y-2">
            <Label htmlFor="image-url">Image URL *</Label>
            <Input
              id="image-url"
              value={imageUrl}
              onChange={(e) => {
                setImageUrl(e.target.value);
                setImageError(false);
              }}
              placeholder="https://example.com/image.jpg"
              data-testid="image-url-input"
            />
            {isGoogleDrive && (
              <p className="text-xs text-accent">✓ Google Drive link detected and will be converted.</p>
            )}
          </div>

          {/* Image Preview */}
          {displayUrl && (
            <div className="relative">
              {!imageError ? (
                <div className="border border-border rounded overflow-hidden bg-muted/30">
                  <img
                    src={displayUrl}
                    alt="Preview"
                    className="max-h-48 mx-auto object-contain"
                    onError={() => setImageError(true)}
                  />
                </div>
              ) : (
                <div className="p-4 text-center text-sm text-destructive bg-destructive/10 rounded">
                  Unable to load image. Please check the URL.
                </div>
              )}
            </div>
          )}

          {/* Caption */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="caption">Caption (optional)</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => setShowCaptionLink(!showCaptionLink)}
              >
                <Link className="h-3 w-3 mr-1" />
                {showCaptionLink ? 'Hide link' : 'Add link'}
              </Button>
            </div>
            <Input
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Describe the image..."
              data-testid="image-caption-input"
            />
            {showCaptionLink && (
              <Input
                value={captionLink}
                onChange={(e) => setCaptionLink(e.target.value)}
                placeholder="https://link-for-caption.com"
                className="text-sm"
                data-testid="caption-link-input"
              />
            )}
          </div>

          {/* Image Courtesy */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="courtesy">Image Courtesy (optional)</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => setShowCourtesyLink(!showCourtesyLink)}
              >
                <Link className="h-3 w-3 mr-1" />
                {showCourtesyLink ? 'Hide link' : 'Add link'}
              </Button>
            </div>
            <Input
              id="courtesy"
              value={courtesy}
              onChange={(e) => setCourtesy(e.target.value)}
              placeholder="Photo by John Doe / Source Name"
              data-testid="image-courtesy-input"
            />
            {showCourtesyLink && (
              <Input
                value={courtesyLink}
                onChange={(e) => setCourtesyLink(e.target.value)}
                placeholder="https://source-website.com"
                className="text-sm"
                data-testid="courtesy-link-input"
              />
            )}
          </div>

          {/* Preview of output */}
          {(caption || courtesy) && (
            <div className="p-3 bg-muted/30 rounded border border-border">
              <p className="text-xs text-muted-foreground mb-1">Preview:</p>
              <div className="text-center text-sm">
                {caption && (
                  <div className={captionLink ? 'underline' : ''}>
                    {caption}
                  </div>
                )}
                {courtesy && (
                  <div className="italic text-muted-foreground">
                    Image courtesy: {courtesyLink ? <span className="underline">{courtesy}</span> : courtesy}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t border-border pt-4 mt-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleInsert} 
            disabled={!displayUrl || imageError}
            data-testid="insert-image-btn"
          >
            Insert Image
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ImageInsertDialog;
